import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkUploadModal = ({ isOpen, onClose, players, onBulkUpload }) => {
  const fileInputRef = useRef(null);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const playerOptions = players?.map((player) => ({
    value: player?.id,
    label: `${player?.name} (${player?.mcaId})`,
  }));

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const validFiles = files?.filter((file) => {
      const maxSize = 10 * 1024 * 1024;
      return file?.size <= maxSize;
    });

    if (validFiles?.length !== files?.length) {
      alert('Some files exceed 10MB limit and were excluded.');
    }

    setSelectedFiles(validFiles);
  };

  const handleBulkUpload = () => {
    if (!selectedPlayer || selectedFiles?.length === 0) {
      alert('Please select a player and at least one file.');
      return;
    }

    setIsUploading(true);

    setTimeout(() => {
      onBulkUpload(selectedPlayer, selectedFiles);
      setIsUploading(false);
      setSelectedPlayer('');
      setSelectedFiles([]);
      onClose();
    }, 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-card rounded-none sm:rounded-lg shadow-lg max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Upload" size={20} className="text-primary" />
            Bulk Document Upload
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <Select
              label="Select Player"
              placeholder="Choose a player"
              options={playerOptions}
              value={selectedPlayer}
              onChange={setSelectedPlayer}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Multiple Files
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <Icon
                name="Files"
                size={32}
                className="mx-auto mb-3 text-muted-foreground"
              />
              <p className="text-sm text-foreground mb-3">
                Select multiple documents at once
              </p>
              <Button
                variant="outline"
                onClick={() => fileInputRef?.current?.click()}
              >
                Choose Files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
              />
              <p className="text-xs text-muted-foreground mt-3">
                Max 10MB per file • PDF, JPG, PNG
              </p>
            </div>
          </div>

          {selectedFiles?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">
                Selected Files ({selectedFiles?.length})
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {selectedFiles?.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon name="FileText" size={18} className="text-primary flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">
                          {file?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file?.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setSelectedFiles(selectedFiles?.filter((_, i) => i !== index))
                      }
                      className="p-1 hover:bg-muted rounded transition-colors flex-shrink-0"
                    >
                      <Icon name="X" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">Bulk Upload Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Name files clearly (e.g., aadhar.pdf, birth_cert.jpg)</li>
                  <li>System will auto-detect document types from filenames</li>
                  <li>You can upload up to 5 documents per player at once</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleBulkUpload}
            loading={isUploading}
            disabled={!selectedPlayer || selectedFiles?.length === 0}
          >
            Upload {selectedFiles?.length} File{selectedFiles?.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;